// Concern → test mapping for Build your own package.
//
// The v5 reference ships its own 27 tests with invented prices and a
// caption admitting as much ("Sample prices for the prototype"). The
// concern groups and the concern → test mapping below are the
// reference's — that clinical shape is the substance of the page — but
// every test is resolved to a real record in our eDOS catalogue, with
// its real id, name and MRP, so nothing on the page is made up.
//
// Each one was resolved by search and then checked by hand, because the
// obvious match is often wrong: "Fasting Blood Sugar" first matched the
// TrustLab PCOS Assessment package, "Electrolytes" matched a HealthPro
// package, "Insulin" matched Insulin *Antibodies* (₹3,360) rather than
// Insulin-Fasting (₹750), and both Testosterone and PSA matched the
// Free assay rather than the Total. Shipping any of those would have put
// a clinically wrong test into a patient's basket.
//
// Prices are a snapshot taken on 2026-11-04. There is no endpoint that
// returns many tests by id in one call, and the full catalogue is an 8 MB
// response, so they cannot be refreshed per page load — see API-NOTES
// 2.39. The page labels the total as an estimate confirmed after doctor
// review, which is what the reference does too.

export const TESTS = {
  cbc:      { id: 539,  name: 'CBC Profile',                               price: 600 },
  esr:      { id: 836,  name: 'Erythrocyte Sedimentation Rate (ESR)',      price: 150 },
  fbs:      { id: 1003, name: 'Glucose-Fasting',                           price: 75,   fasting: true },
  hba1c:    { id: 1019, name: 'Glycated Haemoglobin (GHb/HbA1c)',          price: 450 },
  lipid:    { id: 1521, name: 'Lipid Profile-Essential',                   price: 600,  fasting: true },
  lft:      { id: 1524, name: 'Liver Function-Essential',                  price: 700 },
  kft:      { id: 1477, name: 'Kidney Function Test-I (KFT-I)',            price: 700 },
  urine:    { id: 648,  name: 'Urine Routine Examination-Essential',       price: 120 },
  thyroid:  { id: 1909, name: 'Thyroid Profile with T3/T4 Ratio',          price: 425 },
  vitd:     { id: 2029, name: 'Vitamin D (25-Hydroxy)',                    price: 1500 },
  b12:      { id: 2028, name: 'Vitamin B12 (cyanocobalamin)',              price: 850 },
  iron:     { id: 1443, name: 'Iron',                                      price: 300 },
  ferritin: { id: 870,  name: 'Ferritin',                                  price: 720 },
  calcium:  { id: 504,  name: 'Calcium',                                   price: 250 },
  uric:     { id: 1991, name: 'Uric Acid',                                 price: 275 },
  crp:      { id: 667,  name: 'C-Reactive Protein-High Sensitive (hsCRP)', price: 770 },
  lytes:    { id: 807,  name: 'Electrolyte Profile-Essential',             price: 360 },
  malb:     { id: 1572, name: 'Microalbumin',                              price: 350 },
  lhfsh:    { id: 1514, name: 'LH/FSH Ratio',                              price: 1000 },
  testo:    { id: 1900, name: 'Testosterone-Total',                        price: 600 },
  prl:      { id: 1746, name: 'Prolactin',                                 price: 500 },
  psa:      { id: 1748, name: 'Prostate Specific Antigen-Total (PSA-Total)', price: 750 },
  ra:       { id: 1791, name: 'Rheumatoid Factor-Rapid',                   price: 575 },
  insulin:  { id: 1428, name: 'Insulin-Fasting',                           price: 750,  fasting: true },
  homo:     { id: 1202, name: 'Homocysteine',                              price: 825 },
  amy:      { id: 314,  name: 'Amylase',                                   price: 500 },
  stool:    { id: 1861, name: 'Stool Examination-Essential',               price: 200 },
};

export const GROUPS = [
  {
    t: 'A part of my body',
    s: 'Organs or systems you want checked',
    items: [
      { k: 'heart',    l: 'Heart',                tests: ['lipid', 'crp', 'fbs', 'homo'] },
      { k: 'liver',    l: 'Liver',                tests: ['lft', 'lipid'] },
      { k: 'kidney',   l: 'Kidneys',              tests: ['kft', 'urine', 'lytes', 'uric'] },
      { k: 'thyroid',  l: 'Thyroid',              tests: ['thyroid'] },
      { k: 'bones',    l: 'Bones & joints',       tests: ['vitd', 'calcium', 'uric', 'ra'] },
      { k: 'blood',    l: 'Blood',                tests: ['cbc', 'esr', 'iron'] },
      { k: 'gut',      l: 'Stomach & digestion',  tests: ['stool', 'lft', 'amy'] },
      { k: 'hormones', l: 'Hormones',             tests: ['thyroid', 'prl', 'lhfsh', 'testo'] },
    ],
  },
  {
    t: 'A condition I have or worry about',
    s: 'Diagnosed, or running in your family',
    items: [
      { k: 'diabetes', l: 'Diabetes',             tests: ['fbs', 'hba1c', 'malb', 'kft', 'lipid'] },
      { k: 'bp',       l: 'High blood pressure',  tests: ['kft', 'lytes', 'lipid', 'urine'] },
      { k: 'chol',     l: 'High cholesterol',     tests: ['lipid', 'crp', 'lft'] },
      { k: 'pcos',     l: 'PCOS / PCOD',          tests: ['lhfsh', 'prl', 'testo', 'insulin', 'thyroid'] },
      { k: 'anaemia',  l: 'Anaemia',              tests: ['cbc', 'iron', 'ferritin', 'b12'] },
      { k: 'fatty',    l: 'Fatty liver',          tests: ['lft', 'lipid', 'insulin'] },
    ],
  },
  {
    t: 'Something I have been feeling',
    s: 'Symptoms you have noticed',
    items: [
      { k: 'tired',   l: 'Tiredness',             tests: ['cbc', 'thyroid', 'vitd', 'b12', 'iron', 'fbs'] },
      { k: 'hair',    l: 'Hair fall',             tests: ['thyroid', 'iron', 'ferritin', 'vitd', 'b12'] },
      { k: 'infect',  l: 'Frequent infections',   tests: ['cbc', 'esr', 'urine', 'fbs'] },
      { k: 'joint',   l: 'Joint pain',            tests: ['vitd', 'calcium', 'uric', 'ra', 'esr'] },
      { k: 'weight',  l: 'Weight gain',           tests: ['thyroid', 'fbs', 'insulin', 'lipid'] },
      { k: 'periods', l: 'Irregular periods',     tests: ['thyroid', 'prl', 'lhfsh'] },
    ],
  },
  {
    t: 'My stage of life',
    s: 'Checks that matter at your age',
    items: [
      { k: 'senior', l: 'Over 60',          tests: ['cbc', 'fbs', 'hba1c', 'lipid', 'kft', 'lft', 'vitd', 'thyroid', 'psa'] },
      { k: 'women',  l: 'Women’s health', tests: ['cbc', 'iron', 'thyroid', 'vitd', 'calcium'] },
      { k: 'men',    l: 'Men’s health',   tests: ['cbc', 'lipid', 'fbs', 'testo', 'psa'] },
      { k: 'annual', l: 'Yearly check-up',  tests: ['cbc', 'fbs', 'lipid', 'lft', 'kft', 'urine'] },
    ],
  },
  {
    t: 'My lifestyle',
    s: 'Habits that affect what to check',
    items: [
      { k: 'alcohol', l: 'Drink alcohol regularly',    tests: ['lft', 'lipid', 'uric', 'cbc'] },
      { k: 'smoke',   l: 'Smoke',                      tests: ['cbc', 'lipid', 'crp'] },
      { k: 'desk',    l: 'Desk job, little sun',       tests: ['vitd', 'b12', 'lipid', 'fbs'] },
      { k: 'gym',     l: 'Gym or heavy exercise',      tests: ['cbc', 'kft', 'lytes', 'vitd', 'testo'] },
      { k: 'night',   l: 'Night shifts or poor sleep', tests: ['fbs', 'thyroid', 'vitd', 'lipid'] },
      { k: 'obesity', l: 'Overweight',                 tests: ['fbs', 'hba1c', 'insulin', 'lipid', 'lft', 'thyroid'] },
    ],
  },
];

// Flat lookup of every concern by key.
export const CONCERNS = GROUPS.reduce((map, g) => {
  g.items.forEach((i) => { map[i.k] = i; });
  return map;
}, {});

// Build the test list for a set of concerns. Returns one row per test
// with the concerns that asked for it, so the page can say why each one
// is there. The two sex-specific rules are the reference's.
export const buildPackage = (selected, gender) => {
  const out = new Map();
  selected.forEach((key) => {
    const concern = CONCERNS[key];
    if (!concern) return;
    concern.tests.forEach((t) => {
      if (t === 'psa' && gender === 'Female') return;
      if ((t === 'lhfsh' || t === 'prl') && gender === 'Male' && key !== 'hormones') return;
      if (!out.has(t)) out.set(t, []);
      out.get(t).push(concern.l);
    });
  });
  return [...out.entries()].map(([k, why]) => ({ k, ...TESTS[k], why }));
};

export const PACKAGE_DISCOUNT = 0.15;
export const DISCOUNT_FROM = 4;
